import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { CreateFolderDialog } from '@/components/folders/CreateFolderDialog';
import { FolderTree } from '@/components/folders/FolderTree';
import { StudySearchDialog } from '@/components/folders/StudySearchDialog';
import { GENERAL_SETTINGS_STORAGE_KEY, getSidebarClinicDisplayName, } from '@/components/settings/generalSettingsStorage';
import { ClinicalTagsNav } from '@/components/studies/ClinicalTagsNav';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, } from '@/components/ui/dropdown-menu';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useCurrentUser } from '@/hooks/use-current-user';
import { useIsTcMember } from '@/hooks/use-is-tc-member';
import { useSettings } from '@/hooks/use-settings';
import { useCreateFolder, useFolderTree } from '@/hooks/use-study-folders';
import { useTeleconsultationSidebarCounts } from '@/hooks/use-teleconsultation-sidebar-counts';
import { getAccessToken, getUserPreferences } from '@/lib/api';
import { cn } from '@/lib/utils';
import { CalendarDays, ChevronDown, ClipboardList, FileSearch, FolderOpen, FolderPlus, Inbox, Search, Settings, Video, } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useSearch } from 'wouter';
// Every queue entry names its tab explicitly — `tab=all` included. A bare
// /teleconsultation means "put me back where I was" (the parent entry above
// does exactly that), so "All cases" has to say which view it wants or it
// would reopen the reader's last queue instead.
const tcTabItems = [
    {
        labelKey: 'nav.tc.allCases',
        icon: ClipboardList,
        path: '/teleconsultation?tab=all',
        countKey: 'all',
    },
    {
        labelKey: 'nav.tc.myQueue',
        icon: Inbox,
        path: '/teleconsultation?tab=my_queue',
        countKey: 'myQueue',
    },
    {
        labelKey: 'nav.tc.groupQueue',
        icon: FolderOpen,
        path: '/teleconsultation?tab=group_queue',
        countKey: 'groupQueue',
    },
    {
        labelKey: 'nav.tc.pendingReview',
        icon: FileSearch,
        path: '/teleconsultation?tab=pending_review',
        countKey: 'pendingReview',
    },
];
export function StudiesSidebar({ activeFilter, variant = 'studies', railSection, onFilterChange, activeFolderId, onFolderSelect, folderClinicId, onNavigate, className, counts = {
    all: 0,
    today: 0,
    yesterday: 0,
    pastWeek: 0,
    pastMonth: 0,
    unread: 0,
    statUrgent: 0,
    favorites: 0,
}, }) {
    const { t } = useTranslation();
    const [location, setLocation] = useLocation();
    const searchString = useSearch();
    const isSchedulePage = location === '/schedule';
    const [createFolderDialogOpen, setCreateFolderDialogOpen] = useState(false);
    const [searchDialogOpen, setSearchDialogOpen] = useState(false);
    const { canViewStudies, currentUser, isSuperAdmin } = useCurrentUser();
    // Same entitlement gate as the TopNav TC entry: members or super admins only.
    const { canAccessTeleconsultation } = useIsTcMember();
    const createFolder = useCreateFolder();
    const { data: folderTree } = useFolderTree();
    const { openSettings } = useSettings();
    const { data: tcSidebarData, isPending: tcCountsLoading } = useTeleconsultationSidebarCounts();
    // A super admin's worklist spans every clinic, and a membership-less one has
    // no clinic row to take a name from at all — the header just fell back to the
    // product name. Name the scope they are actually browsing. Anyone else with no
    // clinic row still gets the product name, from the translated `appName` key.
    // The user's own sidebar-name preference wins over both (see clinicName below).
    const clinicNameFromAccount = isSuperAdmin
        ? t('sidebar.allClinics')
        : (currentUser?.clinicRoles?.[0]?.clinicName ?? t('appName'));
    const [sidebarNameOverride, setSidebarNameOverride] = useState(() => getSidebarClinicDisplayName());
    // Hydrate from user-scoped API preferences, re-fetch on user change.
    // Guard with token check to avoid triggering the global 401 redirect.
    useEffect(() => {
        if (!getAccessToken())
            return;
        getUserPreferences()
            .then((prefs) => {
            if (prefs.sidebarClinicDisplayName !== undefined) {
                setSidebarNameOverride(prefs.sidebarClinicDisplayName);
            }
        })
            .catch(() => {
            /* offline — use localStorage value */
        });
    }, [currentUser?.id]);
    // Also listen for in-session changes from the settings page
    useEffect(() => {
        const refresh = () => setSidebarNameOverride(getSidebarClinicDisplayName());
        window.addEventListener('newlumen-general-settings-changed', refresh);
        const onStorage = (e) => {
            if (e.key === GENERAL_SETTINGS_STORAGE_KEY)
                refresh();
        };
        window.addEventListener('storage', onStorage);
        return () => {
            window.removeEventListener('newlumen-general-settings-changed', refresh);
            window.removeEventListener('storage', onStorage);
        };
    }, []);
    const clinicName = sidebarNameOverride.trim() || clinicNameFromAccount;
    const activeFolder = useMemo(() => {
        if (!activeFolderId || !folderTree)
            return null;
        function findFolder(nodes) {
            for (const node of nodes) {
                if (node.id === activeFolderId)
                    return node;
                const found = findFolder(node.children);
                if (found)
                    return found;
            }
            return null;
        }
        return findFolder([...folderTree.personal, ...folderTree.clinic]);
    }, [activeFolderId, folderTree]);
    const handleCreateFolder = (name, scope, parentFolderId, clinicId) => {
        createFolder.mutate({ name, scope, clinicId, parentFolderId }, { onSuccess: () => setCreateFolderDialogOpen(false) });
    };
    const handleTimeFilterClick = (filterId) => {
        onFilterChange?.(filterId);
        // Merge the chosen date window into the worklist's *existing* URL state so
        // the user's other filters (search, modality, sort, …) survive the click.
        // Coming from another page (e.g. Schedule), start clean so we don't carry
        // that page's params onto the worklist.
        const onWorklist = location.startsWith('/study');
        const params = new URLSearchParams(onWorklist ? searchString : '');
        // A new time scope restarts paging.
        params.delete('page');
        params.delete('fromDateTime');
        params.delete('toDateTime');
        const now = new Date();
        switch (filterId) {
            case 'today': {
                const s = new Date(now);
                s.setHours(0, 0, 0, 0);
                params.set('fromDateTime', s.toISOString());
                break;
            }
            case 'yesterday': {
                const y = new Date(now);
                y.setDate(y.getDate() - 1);
                const s = new Date(y);
                s.setHours(0, 0, 0, 0);
                const e = new Date(y);
                e.setHours(23, 59, 59, 999);
                params.set('fromDateTime', s.toISOString());
                params.set('toDateTime', e.toISOString());
                break;
            }
            case 'pastWeek': {
                const w = new Date(now);
                w.setDate(w.getDate() - 7);
                w.setHours(0, 0, 0, 0);
                params.set('fromDateTime', w.toISOString());
                break;
            }
            case 'pastMonth': {
                const m = new Date(now);
                m.setMonth(m.getMonth() - 1);
                m.setHours(0, 0, 0, 0);
                params.set('fromDateTime', m.toISOString());
                break;
            }
            // 'all' clears the date window (params already deleted above).
        }
        const qs = params.toString();
        // On the worklist a time filter is just another filter change, so replace
        // history (consistent with the toolbar filters) rather than stacking an
        // entry per click. Coming from another page it's a real navigation — push.
        setLocation(qs ? `/study/list?${qs}` : '/study/list', { replace: onWorklist });
    };
    const timeFilters = [
        { id: 'all', label: t('filter.all'), count: counts.all },
        { id: 'today', label: t('filter.today'), count: counts.today },
        { id: 'yesterday', label: t('filter.yesterday'), count: counts.yesterday },
        { id: 'pastWeek', label: t('filter.pastWeek'), count: counts.pastWeek },
        { id: 'pastMonth', label: t('filter.pastMonth'), count: counts.pastMonth },
    ];
    const isStudiesPage = location.startsWith('/study');
    const isTcPage = location.startsWith('/teleconsultation');
    const navigate = (path) => {
        setLocation(path);
        onNavigate?.();
    };
    return (_jsxs(_Fragment, { children: [_jsxs("div", { className: cn('border-border flex h-full w-56 flex-col border-r bg-[#0B0F14] [[role=dialog]_&]:w-full', className), children: [_jsxs("div", { className: "border-border border-b px-4 py-4", children: [_jsx("div", { className: "text-foreground truncate text-sm font-semibold tracking-wide uppercase", children: clinicName }), _jsx("div", { className: "text-muted-foreground/60 text-[10px] tracking-widest uppercase", children: t('sidebar.clinicalHud') })] }), _jsx(ScrollArea, { className: "flex-1", children: _jsxs("div", { className: "py-2", children: [_jsx(NavItem, { icon: _jsx(Inbox, { className: "h-4 w-4" }), label: t('nav.studies'), active: isStudiesPage, onClick: () => navigate('/study/list') }), canAccessTeleconsultation && (_jsxs("div", { className: cn('mx-2 flex items-center gap-0.5 rounded-md text-[13px] transition-colors', isTcPage
                                        ? 'bg-teal-600/15 text-teal-400'
                                        : 'text-muted-foreground hover:text-foreground hover:bg-white/5'), children: [_jsxs("button", { type: "button", title: t('sidebar.tcOpen'), className: cn('flex min-w-0 flex-1 items-center justify-between gap-2 rounded-md px-3 py-2 text-left font-medium transition-colors outline-none select-none focus-visible:ring-2 focus-visible:ring-teal-500/40', !isTcPage && 'hover:text-foreground'), onClick: () => navigate('/teleconsultation'), children: [_jsxs("span", { className: "flex min-w-0 items-center gap-2.5", children: [_jsx(Video, { className: "h-4 w-4 shrink-0" }), _jsx("span", { className: "truncate", children: t('nav.teleconsultation') })] }), _jsx("span", { className: "text-muted-foreground/90 min-w-[1.75rem] shrink-0 text-right font-mono text-xs tabular-nums", children: tcCountsLoading ? '\u2013' : (tcSidebarData?.counts.all ?? '\u2013') })] }), _jsxs(DropdownMenu, { children: [_jsx(DropdownMenuTrigger, { asChild: true, children: _jsx(Button, { type: "button", variant: "ghost", size: "icon", className: cn('h-8 w-8 shrink-0 rounded-md pointer-coarse:size-10', isTcPage
                                                            ? 'text-teal-400 hover:bg-teal-500/10 hover:text-teal-300'
                                                            : 'text-muted-foreground hover:text-foreground'), "aria-label": t('sidebar.tcQueuesFilters'), children: _jsx(ChevronDown, { className: "h-3.5 w-3.5 opacity-70" }) }) }), _jsx(DropdownMenuContent, { align: "start", className: "w-56", children: tcTabItems.map((item) => (_jsxs(DropdownMenuItem, { className: "flex cursor-pointer items-center justify-between gap-3 pr-2", onClick: () => navigate(item.path), children: [_jsxs("span", { className: "flex items-center gap-2", children: [_jsx(item.icon, { className: "h-4 w-4 shrink-0" }), t(item.labelKey)] }), _jsx("span", { className: "text-muted-foreground min-w-[2rem] text-right font-mono text-xs tabular-nums", children: tcCountsLoading
                                                                    ? '\u2013'
                                                                    : (tcSidebarData?.counts[item.countKey] ?? '\u2013') })] }, item.path))) })] })] })), _jsx(NavItem, { icon: _jsx(CalendarDays, { className: "h-4 w-4" }), label: t('nav.schedule'), active: isSchedulePage, onClick: () => navigate('/schedule') }), variant === 'schedule' && railSection, variant === 'studies' && (_jsxs(_Fragment, { children: [_jsx("div", { className: "mt-4 px-4 pb-1", children: _jsx("span", { className: "text-muted-foreground/60 text-[10px] font-semibold tracking-widest uppercase", children: t('sidebar.timeFilters') }) }), _jsx("div", { className: "space-y-0.5 px-2", children: timeFilters.map((item) => (_jsxs("button", { onClick: () => handleTimeFilterClick(item.id), className: cn('relative flex w-full items-center justify-between rounded-md px-3 py-1.5 text-[13px] transition-colors pointer-coarse:py-2.5', activeFilter === item.id
                                                    ? // Short inset pill accent (avoids full-height “template” left rule)
                                                        'text-foreground bg-[#3FB4A8]/[0.08] before:pointer-events-none before:absolute before:top-1/2 before:left-2 before:h-[15px] before:w-[2px] before:-translate-y-1/2 before:rounded-full before:bg-[#3FB4A8] before:opacity-80'
                                                    : 'text-muted-foreground hover:text-foreground hover:bg-white/[0.04]'), children: [_jsx("span", { children: item.label }), _jsx("span", { className: "text-muted-foreground/80 min-w-[2rem] text-right font-mono text-xs", children: item.count })] }, item.id))) })] })), variant === 'studies' && canViewStudies && (_jsxs(_Fragment, { children: [_jsxs("div", { className: "mt-4 flex items-center justify-between px-4 pb-1", children: [_jsx("span", { className: "text-muted-foreground/60 text-[10px] font-semibold tracking-widest uppercase", children: t('sidebar.folders') }), _jsxs("div", { className: "flex items-center gap-0.5", children: [_jsx(Button, { variant: "ghost", size: "icon", className: "text-muted-foreground hover:text-foreground -my-1 h-7 w-7 pointer-coarse:size-10", onClick: () => setSearchDialogOpen(true), "aria-label": t('sidebar.searchStudies'), "data-testid": "search-studies-button", children: _jsx(Search, { className: "h-3 w-3" }) }), _jsx(Button, { variant: "ghost", size: "icon", className: "text-muted-foreground hover:text-foreground -my-1 h-7 w-7 pointer-coarse:size-10", onClick: () => setCreateFolderDialogOpen(true), "aria-label": t('sidebar.createNewFolder'), "data-testid": "create-folder-button", children: _jsx(FolderPlus, { className: "h-3 w-3" }) })] })] }), _jsx("div", { className: "px-2", children: _jsx(FolderTree, { activeFolderId: activeFolderId ?? null, onFolderSelect: onFolderSelect ?? (() => { }), clinicId: folderClinicId }) }), _jsx(ClinicalTagsNav, {})] }))] }) }), _jsx("div", { className: "border-border border-t px-2 py-2", children: _jsxs("button", { onClick: () => {
                                openSettings();
                                onNavigate?.();
                            }, className: "text-muted-foreground hover:text-foreground flex w-full items-center gap-2.5 rounded-md px-3 py-2 text-[13px] hover:bg-white/5 pointer-coarse:py-2.5", children: [_jsx(Settings, { className: "h-4 w-4" }), _jsx("span", { children: t('nav.settings') })] }) }), _jsx(CreateFolderDialog, { open: createFolderDialogOpen, onOpenChange: setCreateFolderDialogOpen, onSubmit: handleCreateFolder, defaultScope: "USER", isLoading: createFolder.isPending, folders: folderTree, parentFolder: activeFolder })] }), _jsx(StudySearchDialog, { open: searchDialogOpen, onOpenChange: setSearchDialogOpen, targetFolder: activeFolder })] }));
}
function NavItem({ icon, label, active, onClick, dropdown, }) {
    return (_jsxs("div", { className: cn('mx-2 flex items-center justify-between rounded-md text-[13px] transition-colors', active
            ? 'bg-teal-600/15 text-teal-400'
            : 'text-muted-foreground hover:text-foreground hover:bg-white/5'), children: [_jsxs("button", { className: "flex flex-1 items-center gap-2.5 px-3 py-2 pointer-coarse:py-2.5", onClick: onClick, children: [icon, _jsx("span", { className: "font-medium", children: label })] }), dropdown && _jsx("div", { className: "pr-2", children: dropdown })] }));
}
